import {
    ALL_VIEW,
    TIMECARD_VIEWS,
    withSavedView,
    withViewQuery,
} from 'components/tables/list-table/filter-bar/builtInViews';
import type { FigureRange, FigureRow, WidgetDimension, WidgetFamily, WidgetRecordKind } from 'types/homeWidgetFigures';
import type { HomeWidgetDefinition, SavedViewDefinition } from 'types/homeWidgets';
import { ListKey, listViewPath } from 'types/listViews';
import { escapeRsqlValue } from 'utils/rsql';
import { SAVED_VIEW_LISTS } from './savedViewLists';
import { withDates } from './savedViewQuery';

/** The name of each family's list, as its menu entry reads. */
export const FAMILY_LIST_LABEL: Record<string, string> = {
    TIMECARDS: 'timecards.timecards',
    INVOICES: 'menu.invoices',
    EFFORTS: 'menu.efforts',
    CASES: 'matters.matters',
};

/** The name of the list a widget's figure opens, whichever way the widget was built. */
export function widgetListLabel(definition: HomeWidgetDefinition): string | undefined {
    return definition.source === 'CATALOGUE'
        ? FAMILY_LIST_LABEL[definition.family]
        : SAVED_VIEW_LISTS[definition.listKey]?.labelId;
}

/**
 * The first built-in view of the lists that have several; any other list has {@link ALL_VIEW} alone. The invoices'
 * is `DEFAULT_INVOICE_TAB_URL_KEY`, spelled out because importing it would pull the invoices table into Home.
 */
const FIRST_VIEW: Partial<Record<ListKey, string>> = { timecards: TIMECARD_VIEWS[0].id, invoices: 'main' };

const RECORD_PATH: Record<WidgetRecordKind, string> = { MATTER: '/matter', PARTY: '/contact', EMPLOYEE: '/employee' };

/** Where a widget's figures lead. */
export type WidgetLinks = {
    /** The list showing what the headline figure counted: a catalogue widget's filter over its period, or a view. */
    list: string;
    /** That list narrowed to one row. Nothing for the records with no value on the dimension. */
    row: (row: FigureRow) => string | undefined;
    /** The page of the record a ranked row is. */
    record: (row: FigureRow) => string | undefined;
};

/** A value as a list writes it in a clause: bare while it is an id or a code, quoted and escaped past that. */
function rsqlValue(value: string): string {
    return /^[\w.-]+$/.test(value) ? value : `"${escapeRsqlValue(value)}"`;
}

/**
 * A list's page on a view, showing the filters of `query` and no others. The link names both `mf` and `qf`, empty
 * where the query has none: a list fills a param its address lacks with its own default (the timecards' "My
 * timecards"), and one opened with neither restores the filters the person left it with. Either would show other
 * records than the figure counted.
 */
function opened(listKey: ListKey, builtInId: string, viewId: string | number, query: string): string {
    const params = new URLSearchParams(withViewQuery('', viewId, query));
    for (const key of ['mf', 'qf']) {
        if (!params.has(key)) params.set(key, '');
    }
    return `${listViewPath(listKey, builtInId)}?${params.toString()}`;
}

/**
 * The family's list on its first built-in view, filtered by `clauses`. The link names the view: a list opened
 * without one goes to the person's default view. The clauses go in `mf` joined by `;`, as the list's own filter
 * fields write them, which is what lets the list show each as a chip.
 */
function listLink(family: WidgetFamily, clauses: (string | undefined)[]): string {
    const viewId = FIRST_VIEW[family.listKey] ?? ALL_VIEW.id;
    const query = new URLSearchParams({ mf: clauses.filter(Boolean).join(';') }).toString();
    return opened(family.listKey, viewId, viewId, query);
}

/** The view a saved-view figure counted: a saved view's id or a built-in view's key, and what its list opens it with. */
export type CountedView = { id: number | string; baseViewKey: string; query: string };

/**
 * The list of a saved-view widget, opened in its view. With the view its figure counted, the link carries that
 * view's filters, the period's dates in place of the view's own, so the list shows what was counted. Without it the
 * link names the view alone, and the list opens it as it is saved.
 */
function savedViewLinks(
    definition: SavedViewDefinition,
    period: FigureRange | undefined,
    view: CountedView | undefined
): WidgetLinks | undefined {
    const list = SAVED_VIEW_LISTS[definition.listKey];
    if (!list) return undefined;
    const none = () => undefined;
    if (!view) {
        // A saved view's own page is the list's to find, from any of its built-in views.
        const builtIn = typeof definition.view === 'string' ? definition.view : list.views[0].id;
        const path = listViewPath(definition.listKey, builtIn);
        return { list: `${path}${withSavedView('', String(definition.view))}`, row: none, record: none };
    }
    const query = period && list.dateField ? withDates(view.query, list.dateField, period) : view.query;
    return { list: opened(definition.listKey, view.baseViewKey, view.id, query), row: none, record: none };
}

/**
 * The links of a widget, from the period its figure answered with. For a catalogue widget: nothing when the
 * catalogue does not hold its family, which is how it reads to someone the family's list is closed to; the family's
 * implied filter is left out, since the list applies it itself. For a saved-view widget: its list in that view,
 * exact once `view` is the one its figure counted.
 */
export function widgetLinks(
    catalogue: WidgetFamily[] | undefined,
    definition: HomeWidgetDefinition,
    period: FigureRange | undefined,
    view?: CountedView
): WidgetLinks | undefined {
    if (definition.source === 'SAVED_VIEW') return savedViewLinks(definition, period, view);
    const family = catalogue?.find((candidate) => candidate.key === definition.family);
    if (!family || !period) return undefined;
    const dimension: WidgetDimension | undefined = [...family.breakdownDimensions, ...family.rankedDimensions].find(
        (candidate) => candidate.key === definition.groupBy
    );
    const counted = [
        definition.filter,
        `${family.dateSelector}=ge=${period.from}`,
        `${family.dateSelector}=le=${period.to}`,
    ];
    return {
        list: listLink(family, counted),
        row: ({ key }) =>
            dimension && key !== undefined
                ? listLink(family, [...counted, `${dimension.selector}${dimension.operator}(${rsqlValue(key)})`])
                : undefined,
        record: ({ recordId }) =>
            dimension?.recordKind && recordId !== undefined
                ? `${RECORD_PATH[dimension.recordKind]}/${recordId}`
                : undefined,
    };
}
